/* ==========================================================================
   Burly — product.js
   Product page behavior:
   - Gallery: thumbnail switching, variant-driven media, zoom lightbox
   - Variant picker: resolves the variant from selected option pills, updates
     price / availability / URL / media (variant data embedded as JSON)
   - AJAX add-to-cart through Burly.cart (opens the drawer)
   - Sticky mobile add-to-cart bar (IntersectionObserver on the main button)
   ========================================================================== */

(function () {
  'use strict';

  const root = document.querySelector('[data-product-root]');
  if (!root) return;

  const variantData = JSON.parse(root.querySelector('[data-variant-json]').textContent);
  const moneyFormat = JSON.parse(root.querySelector('[data-money-format]').textContent);

  /* ---- Money formatting (uses the shop's money_format string) ---------- */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, withThousands + '.' + decimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decimals)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* ---- Gallery ----------------------------------------------------------- */
  function showMedia(mediaId) {
    root.querySelectorAll('[data-gallery-main] .product__media-item').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId));
    });
    root.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === String(mediaId));
    });
  }

  root.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) showMedia(thumb.dataset.mediaId);
  });

  /* ---- Lightbox ------------------------------------------------------------ */
  const lightbox = root.querySelector('[data-lightbox]');

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-zoom-trigger], .product__zoom-trigger');
    if (trigger && lightbox) {
      const image = lightbox.querySelector('[data-lightbox-image]');
      image.src = trigger.dataset.zoomSrc;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overlay-open');
      return;
    }
    if (event.target.closest('[data-lightbox-close]') || event.target === lightbox) {
      closeLightbox();
    }
  });

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overlay-open');
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeLightbox();
  });

  /* ---- Variant picker --------------------------------------------------------- */
  function selectedOptions() {
    const options = [];
    root.querySelectorAll('[data-option-index]').forEach((input) => {
      if (input.checked) options[parseInt(input.dataset.optionIndex, 10)] = input.value;
    });
    return options;
  }

  function findVariant(options) {
    return variantData.find((variant) =>
      variant.options.every((value, index) => value === options[index])
    );
  }

  function updateVariant() {
    const options = selectedOptions();
    if (!options.length) return;

    // Update the "Option: value" labels next to each legend.
    options.forEach((value, index) => {
      const label = root.querySelector('[data-option-label="' + index + '"]');
      if (label) label.textContent = value;
    });

    const variant = findVariant(options);
    const atc = root.querySelector('[data-atc]');
    const atcLabel = root.querySelector('[data-atc-label]');
    const stickyButton = root.querySelector('[data-sticky-button]');
    const strings = window.BurlyStrings || {};

    if (!variant) {
      if (atc) atc.disabled = true;
      if (atcLabel) atcLabel.textContent = strings.unavailable || 'Unavailable';
      if (stickyButton) stickyButton.disabled = true;
      return;
    }

    // Hidden id input
    const idInput = root.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    // Price (main + sticky bar)
    const priceEl = root.querySelector('[data-price]');
    if (priceEl) {
      let html = '<div class="price' + (variant.compare_at_price > variant.price ? ' price--on-sale' : '') + '">';
      if (variant.compare_at_price > variant.price) {
        html +=
          '<span class="price__current price__current--sale">' + formatMoney(variant.price) + '</span>' +
          '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
      } else {
        html += '<span class="price__current">' + formatMoney(variant.price) + '</span>';
      }
      priceEl.innerHTML = html + '</div>';
    }
    const stickyPrice = root.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    // Availability
    if (atc) atc.disabled = !variant.available;
    if (atcLabel) {
      atcLabel.textContent = variant.available
        ? strings.addToCart || 'Add to cart'
        : strings.soldOut || 'Sold out';
    }
    if (stickyButton) stickyButton.disabled = !variant.available;

    // Media
    if (variant.media_id) showMedia(variant.media_id);

    // URL (?variant=) so refresh/share keeps the selection
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }

  root.addEventListener('change', (event) => {
    if (event.target.closest('[data-option-index]')) updateVariant();
  });

  /* ---- AJAX add-to-cart ---------------------------------------------------------- */
  const form = root.querySelector('[data-product-form]');
  if (form) {
    form.addEventListener('submit', (event) => {
      if (!window.Burly || !Burly.cart || document.body.dataset.cartType !== 'drawer') return;
      event.preventDefault();

      const atc = root.querySelector('[data-atc]');
      const atcLabel = root.querySelector('[data-atc-label]');
      const original = atcLabel ? atcLabel.textContent : '';
      const strings = window.BurlyStrings || {};

      if (atc) atc.disabled = true;
      if (atcLabel) atcLabel.textContent = strings.adding || 'Adding…';

      Burly.cart
        .add(new FormData(form))
        .then(() => {
          if (atcLabel) atcLabel.textContent = strings.added || 'Added';
        })
        .catch((error) => alert(error.message))
        .finally(() => {
          setTimeout(() => {
            if (atcLabel) atcLabel.textContent = original;
            if (atc) atc.disabled = false;
          }, 1200);
        });
    });
  }

  /* ---- Sticky mobile ATC bar --------------------------------------------------------- */
  const stickyBar = root.querySelector('[data-sticky-atc]');
  const mainAtc = root.querySelector('[data-atc]');

  if (stickyBar && mainAtc && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', String(!show));
      },
      { threshold: 0 }
    );
    observer.observe(mainAtc);
  }
})();
